import { withSpring } from "@-/com.npmjs.react-native-reanimated";
import { isSlideLearnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  HORIZONTAL_TILT_DEGREES,
  LEARN_DECK_RECOVERY_SPRING,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

import { type LearnCardExitDirection, learnCardExitTarget } from "#learn-card-release-motion";
import { resetLearnDeckMotion } from "#use-learn-deck-stage-values";

/**
 * Undo hands the motion back to `presentationKey`, the card an action sent away, waiting where that
 * action left it: off the left or right edge it was swiped or rated to, or above the toolbar after
 * an upward exit. Horizontally it leans as far as a finger dragging it there would tilt it, so it
 * comes back along the same poses. The card on show (`underlayKey`) is drawn beneath it at once,
 * before React has made it the restored card's successor, so nothing blinks in between. A slide card
 * pages back instead: untilted, from one page stride to the left, as the card on show, beside it,
 * pages out to the right.
 *
 * Run on the UI thread with the restore; `slideInLearnCard` starts the return once the card can
 * draw what it shows.
 */
export function placeEnteringLearnCard(
  stage: LearnDeckStageValues,
  {
    direction,
    epoch,
    face,
    presentationKey,
    underlayKey,
  }: {
    direction: LearnCardExitDirection;
    epoch: number;
    face: "back" | "front";
    presentationKey: string;
    underlayKey: string;
  },
): void {
  "worklet";
  resetLearnDeckMotion(stage, face, presentationKey, epoch);
  stage.successorPresentation.set(underlayKey);
  const width = Math.max(stage.cardWidth.get(), 320);
  const from = learnCardExitTarget(stage, presentationKey, direction);
  stage.dragX.set(from.x);
  stage.dragY.set(from.y);
  // Linear in x, like the drag (`tiltForHorizontalOffset`), but unclamped: x and tilt then spring
  // home together and the card holds a dragged card's pose all the way in. A page never tilts.
  stage.tilt.set(
    isSlideLearnCardPresentationKey(presentationKey)
      ? 0
      : (from.x / width) * HORIZONTAL_TILT_DEGREES,
  );
}

/**
 * Bring the card `placeEnteringLearnCard` put off the stage back to rest. A newer motion (another
 * restore, a promotion) or a finger already on it keeps the card where it is.
 */
export function slideInLearnCard(
  stage: LearnDeckStageValues,
  presentationKey: string,
  epoch: number,
): void {
  "worklet";
  if (
    stage.motionEpoch.get() !== epoch ||
    stage.motionPresentation.get().current !== presentationKey ||
    stage.isDragging.get()
  ) {
    return;
  }
  stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
}
